import React, { useState } from 'react';
import {
	Box,
	Grid,
	FormControlLabel,
	Typography,
	Link,
	Divider,
	Switch,
	Button,
	useTheme,
} from '@mui/material';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Prompt } from 'react-router';
import { CheckoutCommentField } from 'common/types';
import { useForm, Controller } from 'react-hook-form';

import { api } from 'common/frontend/api';
import Banner from 'components/Banner';

import SetPageTitle from 'components/Portals/SetPageTitle';
import ContainerBox from 'components/ContainerBox';
import SaveButton from 'common/components/SaveButton';

import CopyableText from 'components/CopyableText';
import { useTranslation, Trans } from 'services/localization';

import { useSelector, useDispatch } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as NotificationActions from 'actions/NotificationActions';

import HasFeature from 'components/features/HasFeature';
import { Routes } from 'routing';
import { CookieSettings } from 'common/modules/atoms/cookies/types';
import CommentFieldSettings from './components/CommentFieldSettings';
import { getOnlineStoreUrl } from 'common/modules/urls';
import { getClientEnv } from 'common/utils/frontUtils';
import { areAllDeliveryOptionsHidden } from 'common/modules/delivery/utils';
import Customizer from './components/Customizer';
import { RiFlashlightFill } from 'react-icons/ri';
import LearnMore from 'components/LearnMore';
import { CUSTOMIZER } from 'common/modules/support/articles';
import ExternalLink from 'components/ExternalLink';
import { setOnboardingActionDone } from 'services/onboarding/utils';

type FormValues = {
	commentField: CheckoutCommentField;
	hideStorePickup: boolean;
	cookieSettings: CookieSettings;
};
const OnlineStoreTab = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const theme = useTheme();

	const [isCustomizerOpen, setIsCustomizerOpen] = useState<boolean>(false);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const shopUrls = useSelector(ShopSelectors.activeShopUrls);
	const isMultiLocationStore = Object.keys(shopUrls?.locationUrls ?? {}).length;
	const activeLocationDeliveryOptions = useSelector(ShopSelectors.activeLocationDeliveryOptions);
	const noVisibleDeliveryOptions = areAllDeliveryOptionsHidden(activeLocationDeliveryOptions);
	const isDeliveryOnlyStore = useSelector(ShopSelectors.isDeliveryOnlyStore);
	const activeShopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const checkoutCommentField = shopPublicInfo?.checkoutCommentField?.[activeLocationId];

	const onlineStoreUrl = getOnlineStoreUrl({
		env: getClientEnv(),
		locationId: isMultiLocationStore ? activeLocationId : null,
		shopUrlDoc: shopUrls,
	});

	const form = useForm<FormValues>({
		defaultValues: {
			hideStorePickup: isDeliveryOnlyStore,
			commentField: checkoutCommentField ?? {
				enabled: false,
				label: { def: '' },
				required: false,
			},
			cookieSettings: {
				default: shopPublicInfo?.cookieSettings?.default ?? 'ENABLE',
				embed: shopPublicInfo?.cookieSettings?.embed ?? 'ENABLE',
			},
		},
		mode: 'onChange',
	});
	const { isSubmitting, isValid, dirtyFields } = form.formState;
	//Workaround for formState.isDirty as it internally requires that all defaultValues have registered inputs which we don't have
	const isDirty = !!Object.keys(dirtyFields).length;

	const handleSubmit = async (data: FormValues) => {
		try {
			const hideStorePickupPath = `hideStorePickup.location.${activeLocationId}`;

			const commentFieldPath = `checkoutCommentField.${activeLocationId}`;

			await api()
				.shops.doc(shopId)
				.public.update({
					...(!!dirtyFields.cookieSettings && {
						cookieSettings: data.cookieSettings,
					}),
					...(!!dirtyFields.hideStorePickup && { [hideStorePickupPath]: data.hideStorePickup }),
					...(!!dirtyFields.commentField && { [commentFieldPath]: data.commentField }),
				});
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	const onCustomizerClick = () => {
		setOnboardingActionDone('APPEARANCE_ONLINE_STORE', activeShopAnalytics);
		setIsCustomizerOpen(true);
	};

	return (
		<>
			<SetPageTitle
				title={t('store.storeSettings', 'Store settings')}
				priority={2}
				rightContent={
					<SaveButton
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						onClick={form.handleSubmit(handleSubmit)}
						t={t}
					/>
				}
			/>
			<Prompt
				when={isDirty}
				message={() =>
					t(
						'common:prompts.unsavedChanges',
						'You have unsaved changes - are you sure you want to leave this page?',
					)
				}
			/>
			{isDeliveryOnlyStore && noVisibleDeliveryOptions && (
				<>
					<Box mb={1} />
					<Banner
						variant="warning"
						title={t(
							'delivery.noVisibleDeliveryOptionsWarningTitle',
							`No visible delivery options`,
						)}
						description={
							<span>
								<Trans
									i18nKey="delivery.noVisibleDeliveryOptionsWarningDesc"
									defaults="In delivery only mode, no products will be shown in the online store until you have at least one visible delivery option with products. <deliveryLink>Edit delivery options</deliveryLink>"
									components={{
										deliveryLink: (
											<ReactRouterLink to={Routes.DELIVERY} className={classes.link}>
												{t('delivery.editDeliveryOptions', 'Edit delivery options')}
											</ReactRouterLink>
										),
									}}
								/>
							</span>
						}
					/>
				</>
			)}
			<ContainerBox>
				<Grid container direction="row" alignItems="center" spacing={2}>
					<Grid item xs={12} md={6}>
						<Typography variant="h6">
							{t('channels.online.themeSettings.header', 'Appearance')}
						</Typography>
						<Typography variant="body2">
							{t(
								'channels.online.themeSettings.editTheLookAndFeel',
								'Edit the look & feel of your Online Store',
							)}
						</Typography>
						<Box mt={1.5}>
							<LearnMore
								linkHref={CUSTOMIZER}
								labelVariant={'customizations'}
								alignment="flex-start"
								showIcon={false}
							/>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'flex-end',
								[theme.breakpoints.down('sm')]: {
									justifyContent: 'flex-start',
								},
							}}
						>
							<Button variant="outlined" onClick={onCustomizerClick}>
								<>
									<Box mr={1}>
										<RiFlashlightFill size={20} />
									</Box>
									{t('common:actions.customize', 'Customize')}
								</>
							</Button>
						</Box>
					</Grid>
				</Grid>
			</ContainerBox>

			<ContainerBox>
				<Box mb={4}>
					<Box
						display="flex"
						flexDirection="row"
						justifyContent="space-between"
						alignItems="flex-start"
					>
						<Typography variant="h6" gutterBottom>
							{t('common:words.url', 'URL')}
						</Typography>
						<Link href={onlineStoreUrl} target="_blank">
							{t('channels.urls.viewStore', 'View store')}
						</Link>
					</Box>
					<CopyableText text={onlineStoreUrl} />
					<HasFeature requiredFeature="DOMAINS">
						<Box mt={2}>
							<Typography variant="body2" color="secondary">
								<Trans
									i18nKey="channels.urls.editUrlsDescripition"
									defaults="Edit the URLs of your account under <accountLink>Account domain settings</accountLink>"
									components={{
										accountLink: (
											<ReactRouterLink to={Routes.ACCOUNT_DOMAINS} className={classes.link}>
												{t('channels.urls.accountDomainSettings', 'Account domain settings')}
											</ReactRouterLink>
										),
									}}
								/>
							</Typography>
						</Box>
					</HasFeature>
				</Box>

				<Box my={4}>
					<Divider />
				</Box>

				<Typography variant="h6" gutterBottom>
					{t('common:actions.embedStore', 'Embed store')}
				</Typography>

				<Typography variant="body2">
					<Trans
						i18nKey="shop.embeddingInfo"
						components={{
							supportLink: (
								<ExternalLink href="https://support.rentle.io/en/embedding-overview">
									{'embed your Rentle online store to your website'}
								</ExternalLink>
							),
						}}
						defaults="Learn how to <supportLink>embed your Rentle online store to your website</supportLink>"
					/>
					.
				</Typography>
				<Box my={4}>
					<Divider />
				</Box>
				<Typography variant="h6" gutterBottom>
					{t('shop.wpPluginHeader', 'WordPress plugin')}
				</Typography>
				<Typography variant="body2">
					<Trans
						i18nKey="shop.wpPluginInfo"
						defaults="<wp>Read here</wp> how you can add Rentle online store to your WordPress site"
						components={{
							wp: (
								<ExternalLink href="https://support.rentle.io/en/using-the-rentle-wordpress-plugin">
									{'Read here'}
								</ExternalLink>
							),
						}}
					/>
					{'.'}
				</Typography>
			</ContainerBox>

			<HasFeature requiredFeature="CUSTOM_CHECKOUT_FIELD">
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">{t('channels.checkoutHeader', 'Checkout')}</Typography>
							<Typography variant="body2">
								{t('channels.customizeCheckoutHeader', 'Customize your online checkout process')}
							</Typography>
						</Grid>
						<Controller
							control={form.control}
							name={'commentField'}
							render={(renderProps) => (
								<CommentFieldSettings
									value={renderProps.field.value}
									onChange={renderProps.field.onChange}
								/>
							)}
						/>
					</Grid>
				</ContainerBox>
			</HasFeature>

			<HasFeature requiredFeature="DELIVERY_SERVICE">
				<ContainerBox>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">
								{t('channels.online.browsingMode.title', 'Browsing mode')}
							</Typography>
							<Typography variant="body2">
								{t(
									'channels.online.browsingMode.description',
									'Customize the browsing experience for your visitors',
								)}
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								control={form.control}
								name="hideStorePickup"
								render={(renderProps) => (
									<FormControlLabel
										style={{ alignItems: 'center' }}
										control={
											<Switch
												color="primary"
												checked={renderProps.field.value}
												onChange={(e) => renderProps.field.onChange(e.target.checked)}
											/>
										}
										label={t('channels.online.browsingMode.switchLabel', 'Hide store pickup')}
									/>
								)}
							/>
							<Typography variant="body2" className={classes.hideStorePickupSwitchText}>
								{t(
									'channels.online.browsingMode.switchDescription',
									'When active, store pickup will not be available',
								)}
							</Typography>
						</Grid>
					</Grid>
				</ContainerBox>
			</HasFeature>
			{isCustomizerOpen && (
				<Customizer open={isCustomizerOpen} onClose={() => setIsCustomizerOpen(false)} />
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wpLink: {
			color: theme.palette.primary.main,
		},
		link: {
			color: theme.palette.primary.main,
			textDecoration: 'underline',
		},
		hideStorePickupSwitchText: {
			marginLeft: theme.spacing(6),
		},
	}),
);

export default OnlineStoreTab;
